import React, { forwardRef } from "react";
import { DateField, TextField, type TextFieldProps } from "@omega/ui";
import type { CalendarDateKey } from "./htmlDateKey";

/** Пропсы {@link Input}. */
export interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "size"> {
    /** Подпись поля. */
    label?: string;
    /** Обязательное поле. */
    required?: boolean;
    /** Флаг ошибки (подсветка control). Текст ошибки — в `FormField` / `BusinessForm.Field`. */
    error?: boolean;
    /** Подсказка под полем. */
    description?: string;
    /** Декоратор слева. */
    startDecorator?: React.ReactNode;
    /** Декоратор справа. */
    endDecorator?: React.ReactNode;
    className?: string;
    inputClassName?: string;
}

type TextFieldSize = NonNullable<TextFieldProps["size"]>;

/**
 * Текстовое поле shared UI: facade над `@omega/ui` TextField.
 * Floating label (outlined), legacy `error` → `invalid`,
 * `startDecorator`/`endDecorator` → `startAdornment`/`endAdornment`.
 */
const InputBase = forwardRef<HTMLInputElement, InputProps>(function Input(
    {
        label,
        required = false,
        error,
        description,
        startDecorator,
        endDecorator,
        className,
        inputClassName,
        disabled,
        readOnly,
        id,
        ...inputProps
    },
    ref,
) {
    return (
        <TextField
            {...inputProps}
            ref={ref}
            id={id}
            label={label}
            required={required}
            invalid={error === true}
            description={description}
            startAdornment={startDecorator}
            endAdornment={endDecorator}
            className={className}
            inputClassName={inputClassName}
            disabled={disabled}
            readOnly={readOnly}
            fullWidth
            size={"md" satisfies TextFieldSize}
            variant="outlined"
            labelBehavior="floating"
        />
    );
});

InputBase.displayName = "Input";

/** Пропсы {@link InputDate}. */
export interface InputDateProps extends Omit<InputProps, "type" | "value" | "onChange"> {
    value?: CalendarDateKey | null;
    onChange?: (value: string) => void;
}

/** Нативный `input[type="date"]` с календарным значением `yyyy-MM-dd`. */
const InputDate = forwardRef<HTMLInputElement, InputDateProps>(function InputDate(
    {
        label,
        required = false,
        error,
        description,
        startDecorator,
        endDecorator,
        className,
        inputClassName,
        disabled,
        readOnly,
        id,
        value,
        onChange,
        ...rest
    },
    ref,
) {
    return (
        <DateField
            {...rest}
            ref={ref}
            id={id}
            label={label}
            required={required}
            invalid={error === true}
            description={description}
            startAdornment={startDecorator}
            endAdornment={endDecorator}
            className={className}
            inputClassName={inputClassName}
            disabled={disabled}
            readOnly={readOnly}
            fullWidth
            size="md"
            variant="outlined"
            labelBehavior="floating"
            value={value ?? null}
            onValueChange={(next) => {
                onChange?.(next);
            }}
        />
    );
});

InputDate.displayName = "InputDate";

const InputWithSubcomponents = Object.assign(InputBase, { Date: InputDate });
export { InputWithSubcomponents as Input };
